import * as types from 'constants/ActionTypes';
import { setConfig as setApiConfig } from 'js-api-client';
import { updatePurchaseWithReserhubAccount } from './purchase';
import { getCommerceApiClient, setAuthToken } from '../services/commerce-api/commerceApiClient';
import errorMonitoring from '../errorMonitoring';
import { updateLegacyReducersWithConnectedAccounts } from '../adapters/loyalty';
import { updateSiemprePlusData } from './siemprePlus';
import { receiveProfile } from './costapass';

/** Same key as `ReserhubAccountProvider` — cleared when unified session ends for any reason. */
const RESERHUB_LOGIN_FLAG = 'RESERHUB_USER_LOGGED_IN';

/**
 * Connected-accounts list response includes per-provider errors without failing the HTTP request.
 * @param {Object} response - API JSON body
 * @returns {boolean}
 */
function hasDotersTenantUnavailableInConnectedAccountsResponse(response) {
  return (
    Array.isArray(response?.errors) &&
    response.errors.some((e) => e?.provider === 'doters' && e?.code === 'unavailable')
  );
}

/**
 * Single-account endpoint returns HTTP 503 with JSON `{ code: "unavailable", ... }`.
 * @param {Error} error - Thrown by the generated Commerce API client
 * @returns {boolean}
 */
function isDotersUnavailableHttpError(error) {
  if (error?.status !== 503) return false;

  if (error?.code === 'unavailable' || error?.details?.code === 'unavailable') return true;

  if (typeof error?.message === 'string' && error.message.includes('"code":"unavailable"')) {
    return true;
  }

  return false;
}

/** Synthetic error when the connected-accounts HTTP request throws (no API body). */
const FETCH_CONNECTED_ACCOUNTS_FAILED = 'fetch_failed';

/**
 * Thunk return shape aligned with the connected-accounts API: `connectedAccounts` plus `errors`.
 * @param {Array} [connectedAccounts]
 * @param {Array} [errors]
 * @returns {{ connectedAccounts: Array, errors: Array }}
 */
function buildFetchConnectedAccountsResult(connectedAccounts = [], errors = []) {
  return {
    connectedAccounts: Array.isArray(connectedAccounts) ? connectedAccounts : [],
    errors: Array.isArray(errors) ? errors : [],
  };
}

/**
 * Generation of the Reserhub session currently in effect.
 *
 * Owned here rather than by a caller so that every account request is tied to the session that
 * started it without the caller opting in: a request captures the generation when it begins and
 * commits nothing once that generation is stale. Callers outside the provider — the OTP activation
 * flows — get the same protection for free.
 */
let sessionGeneration = 0;

/**
 * Revision of the most recently started user request.
 *
 * The session generation alone cannot order two requests started under the *same* session: the
 * provider refreshes the user on entering purchase-complete while `PurchaseCompleteAccountActivation`
 * is still mounted, and validating an OTP starts a second one. Both would be free to write, so the
 * older anonymous response landing last would clear the credential the newer one just obtained.
 */
let latestUserRequest = 0;

/**
 * Mark the session in effect as replaced, invalidating every account request already in flight.
 *
 * Called on every native session change, login and logout alike: a request that started under the
 * previous session must not write user, credentials, purchase attribution or loading flags into the
 * one that replaced it.
 *
 * @returns {number} The new generation.
 */
export function invalidateReserhubSession() {
  sessionGeneration += 1;
  return sessionGeneration;
}

/**
 * The generation of the session currently in effect.
 * @returns {number} Current generation.
 */
export function currentReserhubSession() {
  return sessionGeneration;
}

/**
 * The revision of the most recently started user request.
 *
 * Read straight after dispatching `fetchReserhubUser` to learn that request's own revision: the
 * thunk assigns it synchronously, before its first await.
 *
 * @returns {number} Current revision.
 */
export function currentUserRequest() {
  return latestUserRequest;
}

/**
 * Show or hide the Doters-unavailable-for-tenant dialog (after forced unified logout).
 * @param {boolean} visible - Whether the dialog is visible
 * @returns {Object} The action
 */
export function setReserhubDotersUnavailableDialogVisible(visible) {
  return {
    type: types.SET_RESERHUB_DOTERS_UNAVAILABLE_DIALOG_VISIBLE,
    visible,
  };
}

/**
 * Set Reserhub user in the state
 * @param {Object|null} user - The user data from Reserhub
 * @param {Object} [options]
 * @param {boolean} [options.isAnonymous] - The account request reliably resolved as anonymous.
 * @returns {Object} The action
 */
export function setReserhubUser(user, { isAnonymous = false } = {}) {
  return {
    type: types.SET_RESERHUB_USER,
    user,
    ...(isAnonymous && { isAnonymous: true }),
  };
}

/**
 * Clear Reserhub user from the state (logout)
 * @param {Object} [options]
 * @param {boolean} [options.isAnonymous] - Whether this is a completed logout rather than a handover.
 * @returns {Object} The action
 */
export function clearReserhubUser({ isAnonymous = true } = {}) {
  return {
    type: types.CLEAR_RESERHUB_USER,
    ...(!isAnonymous && { isAnonymous: false }),
  };
}

/**
 * Set loading state for Reserhub user data operations
 * @param {boolean} isLoading - The loading state for user data
 * @returns {Object} The action
 */
export function setReserhubUserLoading(isLoading) {
  return {
    type: types.SET_RESERHUB_USER_LOADING,
    isLoading,
  };
}

/**
 * Set Reserhub next purchase in the state
 * @param {Object} nextPurchase - The next purchase data from Reserhub
 * @returns {Object} The action
 */
export function setReserhubNextPurchase(nextPurchase) {
  return {
    type: types.SET_RESERHUB_NEXT_PURCHASE,
    nextPurchase,
  };
}

/**
 * Clear Reserhub next purchase from the state
 * @returns {Object} The action
 */
export function clearReserhubNextPurchase() {
  return {
    type: types.CLEAR_RESERHUB_NEXT_PURCHASE,
  };
}

/**
 * Set loading state for Reserhub next purchase data operations
 * @param {boolean} isLoading - The loading state for next purchase data
 * @returns {Object} The action
 */
export function setReserhubNextPurchaseLoading(isLoading) {
  return {
    type: types.SET_RESERHUB_NEXT_PURCHASE_LOADING,
    isLoading,
  };
}

/**
 * Fetch Reserhub account next purchase
 * @returns {Function} Thunk action
 */
export function fetchReserhubAccountNextPurchase() {
  return async (dispatch) => {
    const generation = sessionGeneration;
    const isStale = () => generation !== sessionGeneration;

    dispatch(setReserhubNextPurchaseLoading(true));
    try {
      const response = await getCommerceApiClient().accounts.getUpcomingTrips();
      const nextPurchase = response.upcomingTrips?.[0]?.purchase;
      // The session ended or was replaced while this was in flight; its data is not this session's.
      if (isStale()) return nextPurchase;

      if (nextPurchase) {
        dispatch(setReserhubNextPurchase(nextPurchase));
      }
      return nextPurchase;
    } catch (error) {
      if (error.status === 500)
        errorMonitoring.notify({
          error,
          info: {
            message: 'Error fetching Reserhub account next purchase',
          },
        });
      if (!isStale()) dispatch(clearReserhubNextPurchase());
      return null;
    } finally {
      // Withheld when superseded, unlike the wallet and connected-account flags below. The reducer
      // turns this action into `isNextPurchaseFetched: true`, and the effect that loads the next
      // purchase gates on that being false — so a stale request settling it closes the handover's
      // reset and the incoming session never loads its trip. Safe to withhold because every
      // invalidation path now settles these two indicators itself: a handover resets them, a
      // teardown clears them.
      if (!isStale()) dispatch(setReserhubNextPurchaseLoading(false));
    }
  };
}

/**
 * Set loading state for connected accounts
 * @param {boolean} isLoading - The loading state
 * @returns {Object} The action
 */
export function setReserhubConnectedAccountsLoading(isLoading) {
  return {
    type: types.SET_RESERHUB_CONNECTED_ACCOUNTS_LOADING,
    isLoading,
  };
}

/**
 * Set connected accounts in the state
 * @param {Array} connectedAccounts - The connected accounts data
 * @returns {Object} The action
 */
export function setReserhubConnectedAccounts(connectedAccounts) {
  return {
    type: types.SET_RESERHUB_CONNECTED_ACCOUNTS,
    connectedAccounts,
  };
}

/**
 * Set loading state for wallets
 * @param {boolean} isLoading - The loading state
 * @returns {Object} The action
 */
export function setReserhubWalletsLoading(isLoading) {
  return {
    type: types.SET_RESERHUB_WALLETS_LOADING,
    isLoading,
  };
}

/**
 * Set wallets in the state
 * @param {Array} wallets - The wallets data
 * @returns {Object} The action
 */
export function setReserhubWallets(wallets) {
  return {
    type: types.SET_RESERHUB_WALLETS,
    wallets,
  };
}

/**
 * Clear all Reserhub data from the state
 * @param {Object} [options]
 * @param {boolean} [options.preserveCostapassProfile] - When true, keeps CostaPass profile in state (e.g. Doters-tenant-unavailable logout).
 * @returns {Function} Thunk action
 */
/**
 * Clear the next purchase and mark it as never fetched, for a session that is being replaced.
 *
 * `clearReserhubNextPurchase` leaves `isNextPurchaseFetched: true`, which is right at teardown but
 * wrong for a handover: the incoming session would inherit "already fetched, nothing there".
 *
 * @returns {Object} The action
 */
export function resetReserhubNextPurchase() {
  return {
    type: types.RESET_RESERHUB_NEXT_PURCHASE,
  };
}

export function clearAllReserhubData(options = {}) {
  const { preserveCostapassProfile = false, forHandover = false } = options;
  return async (dispatch) => {
    // Every teardown invalidates the session, not only the native logout listener. A request still
    // in flight when the session ends would otherwise remain current and repopulate the user,
    // `userAuthToken` and the purchase attribution moments after this cleared them — reachable from
    // the account menu's logout and from both Doters teardowns, each of which awaits a network call
    // before clearing.
    const generation = invalidateReserhubSession();
    const isCurrent = () => generation === sessionGeneration;

    localStorage.removeItem(RESERHUB_LOGIN_FLAG);
    dispatch(clearReserhubUser({ isAnonymous: !forHandover }));
    dispatch(forHandover ? resetReserhubNextPurchase() : clearReserhubNextPurchase());
    dispatch(setReserhubConnectedAccounts([]));
    dispatch(setReserhubWallets([]));
    dispatch(updateSiemprePlusData(null, 'doters'));
    if (!preserveCostapassProfile) {
      dispatch(receiveProfile(null, 'costapass', null));
    }
    dispatch(setReserhubDotersUnavailableDialogVisible(false));

    // Credentials are cleared *before* any await. After one, a newer handover may already have
    // installed its own, and this teardown resuming would wipe them. The accounts client keeps its
    // own credential when the native host supplied a bearer token, so both are dropped here.
    setApiConfig({ userAuthToken: null, withCredentials: false });
    setAuthToken(null);

    // Detaching after the credentials are gone is also what makes it a detach: the server derives
    // the account from the Authorization header, so an update issued while the previous session's
    // token was still installed would simply re-derive that same account.
    //
    // Awaited on a handover so it cannot still be in flight when the incoming session attributes the
    // same purchase; `isCurrent` stops a superseded response from publishing over that session.
    const detachPurchase = dispatch(
      updatePurchaseWithReserhubAccount({ accountId: null, isCurrent }),
    );
    if (forHandover) return detachPurchase;
  };
}

/**
 * Clear only Doters-related state and remove Doters from connected accounts (no API logout).
 * @returns {Function} Thunk action
 */
export function clearDotersFromState() {
  return (dispatch, getState) => {
    dispatch(updateSiemprePlusData(null, 'doters'));
    const connectedAccounts = getState().reserhubAccount.get('connectedAccounts') || [];
    const withoutDoters = connectedAccounts.filter((account) => account.provider !== 'doters');
    dispatch(setReserhubConnectedAccounts(withoutDoters));
  };
}

/**
 * End unified session when Doters data is not available for this tenant (503 / errors[]).
 * Shows a fixed copy alert and clears local state even if the logout request fails.
 * @returns {Function} Thunk action
 */
export function logoutReserhubDueToDotersTenantUnavailable() {
  return async (dispatch) => {
    try {
      await getCommerceApiClient().accounts.logout('GET');
    } catch {
      // Local state is still cleared below; API logout is best-effort.
    }
    await dispatch(clearAllReserhubData({ preserveCostapassProfile: true }));
    dispatch(setReserhubDotersUnavailableDialogVisible(true));
  };
}

/**
 * End unified session when Doters account refresh is unauthorized.
 * @returns {Function} Thunk action
 */
export function logoutReserhubDueToDotersUnauthorized() {
  return async (dispatch) => {
    try {
      await getCommerceApiClient().accounts.logout('GET');
    } catch {
      // Local state is still cleared below; API logout is best-effort.
    }
    await dispatch(clearAllReserhubData({ preserveCostapassProfile: true }));
  };
}

/**
 * Fetch wallets for the current user
 * @returns {Function} Thunk action
 */
export function fetchWallets() {
  return async (dispatch) => {
    const generation = sessionGeneration;
    const isStale = () => generation !== sessionGeneration;

    dispatch(setReserhubWalletsLoading(true));
    try {
      const response = await getCommerceApiClient().accounts.getAuthUserWallets();
      const wallets = response.wallets || [];
      // The session ended or was replaced while this was in flight; its data is not this session's.
      if (isStale()) return wallets;

      dispatch(setReserhubWallets(wallets));
      return wallets;
    } catch (error) {
      if (error.status === 500)
        errorMonitoring.notify({
          error,
          info: {
            message: 'Error fetching wallets',
          },
        });
      if (!isStale()) dispatch(setReserhubWallets([]));
      return null;
    } finally {
      // Settled unconditionally, unlike the user request's own flags. Invalidation has two sources
      // with different consequences: a teardown settles every flag itself, but a native *login*
      // settles none — so withholding here would strand this flag true for the life of the page,
      // and nothing re-dispatches these requests to clear it. Reporting a superseded request as
      // finished is the lesser error; its data is already withheld above.
      dispatch(setReserhubWalletsLoading(false));
    }
  };
}

/**
 * Fetch a single connected account by provider and update state (e.g. to refresh Doters balance).
 * @param {string} provider - The provider to fetch (e.g. 'doters')
 * @returns {Function} Thunk action
 */
export function fetchConnectedAccountByProvider(provider) {
  return async (dispatch, getState) => {
    const generation = sessionGeneration;
    const isStale = () => generation !== sessionGeneration;

    dispatch(setReserhubConnectedAccountsLoading(true));
    try {
      const response = await getCommerceApiClient().accounts.getAuthUserConnectedAccount(provider);
      const account = response?.connectedAccount ?? response;
      // The session ended or was replaced while this was in flight; its data is not this session's.
      if (isStale()) return null;

      const connectedAccounts = getState().reserhubAccount.get('connectedAccounts') || [];
      if (!account || account.provider !== provider) {
        const withoutProvider = connectedAccounts.filter((a) => a.provider !== provider);
        dispatch(setReserhubConnectedAccounts(withoutProvider));
        updateLegacyReducersWithConnectedAccounts(dispatch, withoutProvider);
        return null;
      }
      const updatedAccounts = connectedAccounts
        .filter((a) => a.provider !== provider)
        .concat(account);
      dispatch(setReserhubConnectedAccounts(updatedAccounts));
      updateLegacyReducersWithConnectedAccounts(dispatch, [account]);
      return account;
    } catch (error) {
      if (error.status === 500) {
        errorMonitoring.notify({
          error,
          info: {
            message: `Error fetching connected account for provider: ${provider}`,
          },
        });
      }
      // A stale failure must not end the session that replaced this request.
      if (isStale()) return null;

      if (provider === 'doters' && error.status === 401) {
        await dispatch(logoutReserhubDueToDotersUnauthorized());
      }
      if (provider === 'doters' && isDotersUnavailableHttpError(error)) {
        await dispatch(logoutReserhubDueToDotersTenantUnavailable());
      }
      return null;
    } finally {
      // Settled unconditionally, unlike the user request's own flags. Invalidation has two sources
      // with different consequences: a teardown settles every flag itself, but a native *login*
      // settles none — so withholding here would strand this flag true for the life of the page,
      // and nothing re-dispatches these requests to clear it. Reporting a superseded request as
      // finished is the lesser error; its data is already withheld above.
      dispatch(setReserhubConnectedAccountsLoading(false));
    }
  };
}

/**
 * Fetch connected accounts for the current user
 * @param {object} [options] - Internal options.
 * @param {Function} [options.isCurrent] - Staleness predicate of the user request that owns this
 *   one. Omitted for a standalone call, which then falls back to the session generation — never
 *   unprotected, just not tied to a particular user request.
 * @returns {Function} Thunk resolving to `{ connectedAccounts, errors }` (same shape as the API body)
 */
export function fetchConnectedAccounts({ isCurrent } = {}) {
  return async (dispatch) => {
    const generation = sessionGeneration;
    const isStale = isCurrent ? () => !isCurrent() : () => generation !== sessionGeneration;

    dispatch(setReserhubConnectedAccountsLoading(true));
    try {
      const response = await getCommerceApiClient().accounts.getAuthUserConnectedAccounts();
      const connectedAccounts = response.connectedAccounts || [];
      const errors = Array.isArray(response.errors) ? response.errors : [];

      if (hasDotersTenantUnavailableInConnectedAccountsResponse(response)) {
        return buildFetchConnectedAccountsResult(connectedAccounts, errors);
      }
      // Writing these would overwrite the newer session's accounts with the previous session's.
      if (isStale()) return buildFetchConnectedAccountsResult(connectedAccounts, errors);

      dispatch(setReserhubConnectedAccounts(connectedAccounts));
      return buildFetchConnectedAccountsResult(connectedAccounts, errors);
    } catch (error) {
      if (error.status === 500)
        errorMonitoring.notify({
          error,
          info: {
            message: 'Error fetching connected accounts',
          },
        });
      if (!isStale()) dispatch(setReserhubConnectedAccounts([]));
      return buildFetchConnectedAccountsResult(
        [],
        [
          {
            code: FETCH_CONNECTED_ACCOUNTS_FAILED,
            message: 'Error fetching connected accounts',
            provider: null,
            cause: error instanceof Error ? error : new Error(String(error)),
          },
        ],
      );
    } finally {
      // The loading flags describe whichever request is current; settling them from a superseded one
      // reports the newer request as finished. The session change that superseded this request has
      // already settled them (`clearAllReserhubData` on logout), or the newer request will.
      if (!isStale()) dispatch(setReserhubConnectedAccountsLoading(false));
    }
  };
}

/**
 * Fetch Reserhub user data
 * @returns {Function} Thunk action
 */
export function fetchReserhubUser() {
  return async (dispatch) => {
    const generation = sessionGeneration;
    latestUserRequest += 1;
    const revision = latestUserRequest;
    // Superseded by a session change, or by a newer user request started under the same session.
    const isCurrent = () => generation === sessionGeneration && revision === latestUserRequest;

    dispatch(setReserhubUserLoading(true));
    try {
      // User profile and connected accounts can be fetched in parallel.
      const [userSettled, connectedSettled] = await Promise.allSettled([
        getCommerceApiClient().accounts.getAuthenticatedUser(),
        dispatch(fetchConnectedAccounts({ isCurrent })),
      ]);

      // The session this request belongs to may have ended, or been replaced by a newer one, while
      // it was in flight. Everything below commits session state — user, `userAuthToken`, purchase
      // attribution — so a superseded response must stop here rather than write and be repaired
      // afterwards: a later repair cannot tell the stale session from the one that just started.
      if (!isCurrent()) return null;

      const user = userSettled.status === 'fulfilled' ? userSettled.value : null;
      const isAnonymous =
        (userSettled.status === 'fulfilled' && user == null) ||
        (userSettled.status === 'rejected' && userSettled.reason?.status === 401);
      const connectedFetchResult =
        connectedSettled.status === 'fulfilled' ? connectedSettled.value : null;

      if (
        connectedFetchResult &&
        hasDotersTenantUnavailableInConnectedAccountsResponse(connectedFetchResult)
      ) {
        await dispatch(logoutReserhubDueToDotersTenantUnavailable());
        return null;
      }

      const connectedAccountsFetchFailed = connectedFetchResult?.errors?.some(
        (e) => e?.code === FETCH_CONNECTED_ACCOUNTS_FAILED,
      );
      const connectedAccounts =
        connectedFetchResult && !connectedAccountsFetchFailed
          ? connectedFetchResult.connectedAccounts
          : null;

      if (user && user.id) {
        dispatch(setReserhubUser(user));
        // withCredentials config is set to false to avoid sending another session cookie to the API.
        setApiConfig({ userAuthToken: user.sessionToken, withCredentials: false });
        const connectedAccountsFetchSucceeded = Array.isArray(connectedAccounts);
        const hasDotersAccount =
          connectedAccountsFetchSucceeded && connectedAccounts.some((c) => c.provider === 'doters');

        // Only update purchase when we confirmed there is no Doters account.
        // Skipping when the fetch failed (null) avoids the same Doters conflict on the error path.
        if (connectedAccountsFetchSucceeded && !hasDotersAccount) {
          dispatch(updatePurchaseWithReserhubAccount({ accountId: user.id, isCurrent }));
        }

        if (connectedAccounts && connectedAccounts.length > 0) {
          updateLegacyReducersWithConnectedAccounts(dispatch, connectedAccounts);
        }
      } else {
        if (isAnonymous) dispatch(setReserhubUser(null, { isAnonymous: true }));
        setApiConfig({ userAuthToken: null, withCredentials: false });
      }

      if (connectedSettled.status === 'rejected') {
        if (connectedSettled.reason?.status !== 401) {
          errorMonitoring.notify({
            error: connectedSettled.reason,
            info: {
              message: 'Error fetching unified account data during user fetch',
            },
          });
        }
      } else if (connectedAccountsFetchFailed) {
        const fetchFailedEntry = connectedFetchResult?.errors?.find(
          (e) => e?.code === FETCH_CONNECTED_ACCOUNTS_FAILED,
        );
        const { cause } = fetchFailedEntry || {};
        const alreadyNotifiedFor500 = cause?.status === 500;
        // 401 is the expected response for anonymous visitors — not an error worth reporting.
        const isExpectedUnauthenticated = cause?.status === 401;
        if (!alreadyNotifiedFor500 && !isExpectedUnauthenticated) {
          const errorForMonitoring =
            cause instanceof Error
              ? cause
              : new Error(fetchFailedEntry?.message || 'Error fetching connected accounts');
          errorMonitoring.notify({
            error: errorForMonitoring,
            info: {
              message: 'Error fetching unified account data during user fetch',
            },
          });
        }
      }

      return user;
    } catch (error) {
      if (error.status === 500)
        errorMonitoring.notify({
          error,
          info: {
            message: 'Error fetching Reserhub user',
          },
        });

      // Same staleness rule on the failure path: clearing credentials and attribution here would
      // tear down whatever session replaced this one.
      if (!isCurrent()) return null;

      setApiConfig({ userAuthToken: null, withCredentials: false });
      dispatch(updatePurchaseWithReserhubAccount({ accountId: null, isCurrent }));
    } finally {
      // `SET_RESERHUB_USER_LOADING(false)` also sets `isUserFetched`, which is what
      // `StoredCardsCheckout` waits on. Settling it from a superseded request would drop the
      // ownership skeleton and flash the guest new-card form while the current request is still in
      // flight. Whatever superseded this request settles the flags instead.
      if (isCurrent()) dispatch(setReserhubUserLoading(false));
    }
  };
}

/**
 * Logout the Reserhub user
 * @returns {Function} Thunk action
 */
export function logoutReserhubUser() {
  return async (dispatch) => {
    dispatch(setReserhubUserLoading(true));
    try {
      await getCommerceApiClient().accounts.logout('GET');
      setApiConfig({ userAuthToken: null, withCredentials: false });
      dispatch(clearAllReserhubData());
      return true;
    } catch (error) {
      if (error.status === 500)
        errorMonitoring.notify({
          error,
          info: {
            message: 'Error logging out Reserhub user',
          },
        });

      // Clear next purchase data even if logout fails
      dispatch(clearReserhubNextPurchase());
      return false;
    } finally {
      dispatch(setReserhubUserLoading(false));
    }
  };
}
